import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

interface ContentWidthProps {
  text: string;
  contentWidth: 's' | 'm' | 'l' | 'f' | '';
  onClick?: (contentWidth: 's' | 'm' | 'l' | 'f' | '') => void;
  className?: string;
}

const contentWidths: Array<'s' | 'm' | 'l' | 'f'> = ['s', 'm', 'l', 'f'];

export const ContentWidth: React.FC<ContentWidthProps> = ({
  contentWidth = 's',
  onClick,
  text = 'Type',
  className,
}) => {
  const [selectedContentWidth, setSelectedContentWidth] = useState(contentWidth);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    s: null,
    m: null,
    l: null,
    f: null,
  });

  useEffect(() => {
    if (contentWidth !== selectedContentWidth) {
      setSelectedContentWidth(contentWidth);
    }
  }, [contentWidth]);

  useLayoutEffect(() => {
    if (selectedContentWidth === '') return;

    const activeEl = optionRefs.current[selectedContentWidth];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedContentWidth]);

  const handleClick = (newContentWidth: 's' | 'm' | 'l' | 'f') => {
    setIsAnimated(true);
    setSelectedContentWidth(newContentWidth);
    if (onClick) onClick(newContentWidth);
  };

  return (
    <>
      <div className={clsx('flex items-center justify-between gap-4', className)}>
        {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

        <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset-0px-0px-2px-0px-rgba(0,0,0,0.12)]">
          {selectedContentWidth !== '' && (
            <div
              className={`absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0] ${
                isAnimated ? 'transition-all duration-300' : ''
              }`}
              style={{
                width: `${highlightStyle.width}px`,
                transform: `translateX(${highlightStyle.left}px)`,
              }}
            >
              <p className="text-button-sm !font-semibold capitalize">{selectedContentWidth}</p>
            </div>
          )}

          {contentWidths.map((sz) => (
            <button
              key={sz}
              ref={(el) => {
                optionRefs.current[sz] = el;
              }}
              onClick={() => handleClick(sz)}
              className="text-button-sm relative z-10 h-8 w-7 cursor-pointer px-2 py-1.5 text-neutral-400 capitalize"
            >
              {sz}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};
